import { useCallback, useState } from "react";
import { useSearchParams } from "react-router-dom";

const EDIT_PARAM = "edit";

export function useEditMode(sectionName?: string) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [pendingSection, setPendingSection] = useState<string | null>(null);
  const [pendingDiscard, setPendingDiscard] = useState<(() => void) | null>(
    null
  );

  const currentEditSection = searchParams.get(EDIT_PARAM) || null;

  const isEditMode = sectionName ? currentEditSection === sectionName : false;

  const setEditMode = useCallback(
    (section: string | null) => {
      const newParams = new URLSearchParams(searchParams);
      if (section) {
        newParams.set(EDIT_PARAM, section);
      } else {
        newParams.delete(EDIT_PARAM);
      }
      setSearchParams(newParams, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  const editMode = useCallback(
    (hasChanges: boolean, onDiscard: () => void): boolean => {
      if (!sectionName) return false;

      if (currentEditSection === sectionName) {
        return true; // Already in edit mode
      }

      if (currentEditSection && hasChanges) {
        // Another section is editing and has changes - show confirmation
        setPendingSection(sectionName);
        setPendingDiscard(() => onDiscard);
        setShowConfirmDialog(true);
        return false; // Need confirmation
      }

      if (currentEditSection) {
        // Another section is editing but no changes - switch immediately
        setEditMode(sectionName);
        return true;
      }

      // No section is editing - allow edit mode
      setEditMode(sectionName);
      return true;
    },
    [currentEditSection, sectionName, setEditMode]
  );

  const viewMode = useCallback(() => {
    if (!sectionName) return;
    // Only release if this section is currently editing
    if (currentEditSection === sectionName) {
      setEditMode(null);
    }
  }, [currentEditSection, sectionName, setEditMode]);

  const confirmDiscard = useCallback(() => {
    if (pendingDiscard && pendingSection) {
      pendingDiscard();
      setEditMode(pendingSection);
      setPendingSection(null);
      setPendingDiscard(null);
      setShowConfirmDialog(false);
    }
  }, [pendingDiscard, pendingSection, setEditMode]);

  const cancelDiscard = useCallback(() => {
    setPendingSection(null);
    setPendingDiscard(null);
    setShowConfirmDialog(false);
  }, []);

  return {
    isEditMode,
    editMode,
    viewMode,
    showConfirmDialog,
    confirmDiscard,
    cancelDiscard,
  };
}
